import { useTranslation } from "react-i18next"

import { Button } from "@f2e/ui/components/base"
import type {
  WebsiteCrawlTargetSchema,
  WebsiteCrawlJobSchema,
} from "@/hooks/api"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface WebsiteCrawlSummaryProps {
  target: WebsiteCrawlTargetSchema | null
  job: WebsiteCrawlJobSchema | null
  status: "loading" | "success" | "error"
  error: unknown
  onRetry: () => void
  onDetails: () => void
}

export const WebsiteCrawlSummary = ({
  target,
  job,
  status,
  error,
  onRetry,
  onDetails,
}: WebsiteCrawlSummaryProps) => {
  const { t, i18n } = useTranslation()
  if (status === "loading") return <span>{t("common.loading")}</span>
  if (status === "error") {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-2">
        {getUserFacingApiErrorMessage(
          error,
          t,
          t("knowledgeBase.treeView.websiteCrawlStateLoadFailed")
        )}
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={onRetry}
        >
          {t("knowledgeBase.treeView.websiteCrawlRetry")}
        </Button>
      </span>
    )
  }

  const jobStatus = job?.status
  const showJobStatus =
    jobStatus &&
    ["PENDING", "RUNNING", "PARTIAL_FAILED", "FAILED", "CANCELED"].includes(
      jobStatus
    )
  const needsAttention =
    jobStatus === "PARTIAL_FAILED" || jobStatus === "FAILED"

  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span>
        {target?.last_synced_at ? (
          <>
            {t("knowledgeBase.treeView.websiteCrawlLastSyncedAt")}{" "}
            <time dateTime={target.last_synced_at}>
              {new Date(target.last_synced_at).toLocaleString(i18n.language)}
            </time>
          </>
        ) : (
          t("knowledgeBase.treeView.websiteCrawlNoSyncRecord")
        )}
      </span>
      {showJobStatus && (
        <span
          className={
            needsAttention ? "text-amber-700 dark:text-amber-400" : undefined
          }
        >
          {t("knowledgeBase.treeView.websiteCrawlStatus")}
          {" · "}
          {t(
            `knowledgeBase.treeView.websiteCrawlJobStatus.${jobStatus.toLowerCase()}`
          )}
        </span>
      )}
      {needsAttention && (
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={onDetails}
        >
          {t("knowledgeBase.treeView.websiteCrawlViewDetails")}
        </Button>
      )}
    </span>
  )
}
